import { useMemo, useState } from "react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  useAdminActivity,
  type AdminActivityEntry,
} from "@/hooks/useAdminActivity";

export const Route = createFileRoute("/admin/activity")({
  component: AdminActivityPage,
});

const ACTION_FILTERS = [
  { value: "", label: "All actions" },
  { value: "set_plan", label: "Set plan" },
  { value: "set_tags", label: "Set tags" },
  { value: "set_plan_notes", label: "Plan notes" },
  { value: "deactivate_user", label: "Deactivate" },
  { value: "restore_user", label: "Restore" },
  { value: "delete_user", label: "Delete" },
  { value: "act_as_start", label: "Act-as start" },
  { value: "act_as_end", label: "Act-as end" },
] as const;

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function displayName(entry: AdminActivityEntry, field: "admin" | "target") {
  const email = field === "admin" ? entry.admin_email : entry.target_email;
  const name =
    field === "admin" ? entry.admin_display_name : entry.target_display_name;
  return name || email || "—";
}

function formatMetadata(m: Record<string, unknown>): string {
  const entries = Object.entries(m);
  if (entries.length === 0) return "";
  return entries
    .map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
}

function AdminActivityPage() {
  const router = useRouter();
  const [action, setAction] = useState("");
  // Stack of page-boundary ids — each entry is the `before_id` used for that
  // page request. Pushing on "Next", popping on "Prev".
  const [cursorStack, setCursorStack] = useState<(number | undefined)[]>([
    undefined,
  ]);
  const currentCursor = cursorStack[cursorStack.length - 1];

  const { data, isLoading, error } = useAdminActivity({
    beforeId: currentCursor,
    action: action || undefined,
    limit: 50,
  });

  const canGoNext = useMemo(() => (data?.length ?? 0) === 50, [data]);
  const canGoPrev = cursorStack.length > 1;

  function handleNext() {
    if (!data || data.length === 0) return;
    const lastId = data[data.length - 1].id;
    setCursorStack((s) => [...s, lastId]);
  }

  function handlePrev() {
    setCursorStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }

  function handleFilterChange(next: string) {
    setAction(next);
    setCursorStack([undefined]);
  }

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => router.history.back()}
        >
          <ArrowLeft className="size-4" />
        </Button>
        <h1 className="text-2xl font-bold">Admin Activity</h1>
      </div>

      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm text-muted-foreground">Filter:</label>
        <select
          value={action}
          onChange={(e) => handleFilterChange(e.target.value)}
          className="rounded border bg-background px-2 py-1 text-sm"
        >
          {ACTION_FILTERS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <Card className="border-destructive mb-4">
          <CardContent className="pt-6 text-destructive">
            {error instanceof Error ? error.message : "Failed to load activity"}
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <p className="text-muted-foreground">Loading activity…</p>
      )}

      {data && (
        <Card>
          <CardHeader>
            <CardTitle>
              {data.length === 0
                ? "No activity"
                : `${data.length} action${data.length === 1 ? "" : "s"}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No admin actions match this filter.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 pr-4 font-medium">When</th>
                      <th className="pb-2 pr-4 font-medium">Admin</th>
                      <th className="pb-2 pr-4 font-medium">Action</th>
                      <th className="pb-2 pr-4 font-medium">Target</th>
                      <th className="pb-2 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((entry) => (
                      <tr
                        key={entry.id}
                        className="border-b last:border-0 hover:bg-muted/50 transition-colors"
                      >
                        <td
                          className="py-2 pr-4 text-muted-foreground whitespace-nowrap"
                          title={new Date(entry.created_at).toLocaleString()}
                        >
                          {relativeTime(entry.created_at)}
                        </td>
                        <td className="py-2 pr-4 font-mono text-xs">
                          {displayName(entry, "admin")}
                        </td>
                        <td className="py-2 pr-4 font-medium">{entry.action}</td>
                        <td className="py-2 pr-4 font-mono text-xs">
                          {displayName(entry, "target")}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground font-mono max-w-md truncate">
                          {formatMetadata(entry.action_metadata)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrev}
          disabled={!canGoPrev}
        >
          ← Newer
        </Button>
        <span className="text-xs text-muted-foreground">
          Page {cursorStack.length}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={handleNext}
          disabled={!canGoNext}
        >
          Older →
        </Button>
      </div>
    </div>
  );
}
